import React, {
  forwardRef,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { formatRelativeTime, getFullDate } from "../../utils/formatUtils";
import { highlightHtmlContent } from "../../utils/htmlUtils";
import { getNoteInitials } from "../../utils/noteUtils";
import PriorityIndicator from "./PriorityIndicator";
import UserStatusIndicator from "../Tooltips/UserStatusIndicator";
// Import refactored popup components
import PopupPortal from "./popups/PopupPortal";
import CalendarPopup from "./popups/CalendarPopup";
import AssigneePopup from "./popups/AssigneePopup";
import StatusPopup from "./popups/StatusPopup";

// Helper for random colors
const getRandomColor = (id) => {
  const colors = ["#1976d2", "#dc3545", "#28a745", "#fd7e14", "#6f42c1", "#20c997", "#e83e8c", "#007bff"];
  return colors[(id || 0) % colors.length];
};

// Helper for initials
const getInitialsHelper = (name) => {
  if (!name) return "";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2);
};

// Helper for default status mapping (without external taskStatuses)
const getDefaultStatus = (statusId) => {
  const defaults = {
    1: { id: 1, name: "To Do", icon: "fa-circle", color: "#6c757d", bgColor: "#e9ecef" },
    2: { id: 2, name: "In Progress", icon: "fa-spinner", color: "#fd7e14", bgColor: "#fff3e0" },
    3: { id: 3, name: "Archived", icon: "fa-archive", color: "#6c757d", bgColor: "#e9ecef" },
    4: { id: 4, name: "Blocked", icon: "fa-times", color: "#dc3545", bgColor: "#f8d7da" },
    5: { id: 5, name: "Done", icon: "fa-check-circle", color: "#28a745", bgColor: "#d4edda" },
  };
  return defaults[statusId] || defaults[1];
};

// Hook to calculate popup position (returns null until calculated)
const usePopupPosition = (triggerRef, isOpen, popupType = "calendar") => {
  const [position, setPosition] = useState(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let popupHeight = 400;
    let popupWidth = 280;
    if (popupType === "assignee") { popupHeight = 350; popupWidth = 240; }
    else if (popupType === "status") { popupHeight = 240; popupWidth = 200; }

    let top = rect.bottom + 5;
    let left = rect.left;
    if (top + popupHeight > viewportHeight) top = rect.top - popupHeight + 80;
    if (left + popupWidth > viewportWidth) left = viewportWidth - popupWidth - 10;
    if (top < 0) top = 10;
    if (left < 0) left = 10;
    setPosition({ top, left });
  }, [triggerRef, popupType]);

  useEffect(() => {
    if (isOpen) {
      calculatePosition();
      window.addEventListener("scroll", calculatePosition, true);
      window.addEventListener("resize", calculatePosition);
      return () => {
        window.removeEventListener("scroll", calculatePosition, true);
        window.removeEventListener("resize", calculatePosition);
      };
    } else {
      setPosition(null);
    }
  }, [isOpen, calculatePosition]);

  return position;
};
const NoteCard = forwardRef((props, ref) => {
  const {
    note,
    displayNotes,
    isOriginalNoteExists,
    selectedRow,
    searchTerm,
    viewMode,
    handleRowClick,
    handleRowDoubleClick,
    handleAddFromRow,
    handleEdit,
    handleDelete,
    handleViewAttachments,
    handleReplyToNote,
    handlePriorityClick,
    handlePriorityMouseEnter,
    handlePriorityMouseLeave,
    handleNoteTextMouseEnter,
    handleNoteTextMouseLeave,
    handleLinkedNoteClick,
    handleLinkedNoteMouseEnter,
    handleLinkedNoteMouseLeave,
    renderCardImageIcon,
    isNoteReply,
    getReplyNoteId,
    userStatusMap,
    loadingUsers,
    getPriorityValue,
    manuallyUpdatedPriorities,
    shouldShowNotePopup,
    handleProjectClick,
    handleJobClick,
    handleTaskClick,
    handleUserNameClick,
    isProjectFiltered,
    isJobFiltered,
    isTaskFiltered,
    isUserNameFiltered,
    taskData = {},
    onTaskDueDateSelect,
    onTaskAssigneeSelect,
    onTaskStatusSelect,
    taskUsers = [],
    taskStatuses = [],
    loadingTaskUsers = false,
    userId,
    refreshNotes,
    job,
    onFilterByProject,
    onFilterByJob,
  } = props;

  // ========== Existing state ==========
  const priorityValue = getPriorityValue(note, manuallyUpdatedPriorities);
  const isReply = note.isReply === 1;
  const originalNoteId = isReply ? getReplyNoteId(note.id) : null;
  const originalNoteExists = isReply && originalNoteId && originalNoteId !== 0 ? isOriginalNoteExists(originalNoteId) : false;
  const shouldShowLink = isReply && originalNoteExists && originalNoteId !== 0;
  const [showJobTooltip, setShowJobTooltip] = useState(false);
  const [showProjectTooltip, setShowProjectTooltip] = useState(false);

  // Task data – for both tasks and notes that have task fields (but UI only for tasks)
  const [selectedDueDate, setSelectedDueDate] = useState(note.dueDate || null);
  const [selectedStartDate, setSelectedStartDate] = useState(note.startDate || null);
  const [selectedEndDate, setSelectedEndDate] = useState(note.endDate || null);
  const [selectedAssignees, setSelectedAssignees] = useState(() => {
    if (note.assignedUsers && note.assignedUsers.length > 0) {
      return note.assignedUsers.map(user => ({ id: user.userId, name: user.fullName || user.username }));
    }
    return note.assigneeId ? [{ id: note.assigneeId, name: note.assigneeName }] : [];
  });

  // ---------- FIXED: selectedStatus initialisation ----------
  const [selectedStatus, setSelectedStatus] = useState(() => {
    // First try to use note.status and fallback to default mapping
    if (note.status) {
      // If taskStatuses is provided and contains the status, use it
      if (taskStatuses?.length) {
        const fromProps = taskStatuses.find(s => s.id === note.status);
        if (fromProps) return fromProps;
      }
      // Otherwise use the built-in default mapping
      return getDefaultStatus(note.status);
    }
    // Ultimate fallback
    return getDefaultStatus(1);
  });

  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsersList, setLoadingUsersList] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [updatingTask, setUpdatingTask] = useState(false);
  const [updatingAssignees, setUpdatingAssignees] = useState(false);

  // Popup state (unified)
  const [activePopup, setActivePopup] = useState(null);
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [calendarMode, setCalendarMode] = useState("dueDate");
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Refs for icons
  const calendarIconRef = useRef(null);
  const assigneeIconRef = useRef(null);
  const statusIconRef = useRef(null);

  // Positions
  const calendarPosition = usePopupPosition(calendarIconRef, activePopup === "dueDate", "calendar");
  const assigneePosition = usePopupPosition(assigneeIconRef, activePopup === "assignee", "assignee");
  const statusPosition = usePopupPosition(statusIconRef, activePopup === "status", "status");

  const apiUrl = process.env.REACT_APP_API_BASE_URL || "";

  // ✅ ONLY treat as task if itemType === "Task"
  const isTask = note.itemType === "Task";

  // ========== Effect to sync with taskStatuses (if loaded later) ==========
  useEffect(() => {
    if (note.status && taskStatuses?.length) {
      const matched = taskStatuses.find(s => s.id === note.status);
      if (matched && selectedStatus.id !== matched.id) {
        setSelectedStatus(matched);
      }
    }
  }, [taskStatuses, note.status, selectedStatus.id]);

  // ========== Existing functions (updateTask, addAssignee, removeAssignee, fetchUsers) ==========
  const updateTask = useCallback(async (updates) => {
    if (!note.taskId) {
      toast.error("Cannot update task: Task ID not found");
      return;
    }
    setUpdatingTask(true);
    let updateType = "";
    if (updates.dueDate !== undefined) updateType = "Due date";
    else if (updates.startDate !== undefined || updates.endDate !== undefined) updateType = "Duration";
    else if (updates.status !== undefined) updateType = "Status";
    try {
      const payload = {
        title: note.title || note.note || "",
        description: note.description || note.note || "",
        startDate: updates.startDate !== undefined ? updates.startDate : (note.startDate || new Date().toISOString()),
        endDate: updates.endDate !== undefined ? updates.endDate : note.endDate,
        dueDate: updates.dueDate !== undefined ? updates.dueDate : selectedDueDate,
        jobId: note.jobId || 0,
        status: selectedStatus?.id || 1,
        ...updates,
      };
      const response = await fetch(`${apiUrl}/api/JobTasks/${note.taskId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to update task: ${response.status}`);
      if (updateType) toast.success(`${updateType} updated successfully`);
      if (updates.dueDate !== undefined && onTaskDueDateSelect) onTaskDueDateSelect(note, updates.dueDate);
      if ((updates.startDate !== undefined || updates.endDate !== undefined) && onTaskDueDateSelect) {
        onTaskDueDateSelect(note, { startDate: updates.startDate, endDate: updates.endDate });
      }
      if (updates.status !== undefined && onTaskStatusSelect) onTaskStatusSelect(note, { id: updates.status });
      if (refreshNotes) refreshNotes();
    } catch (error) {
      console.error(error);
      toast.error(`Failed to update ${updateType.toLowerCase() || "task"}`);
    } finally {
      setUpdatingTask(false);
    }
  }, [note, selectedDueDate, selectedStatus, apiUrl, onTaskDueDateSelect, onTaskStatusSelect, refreshNotes]);

  const addAssignee = useCallback(async (user) => {
    if (!note.taskId || !userId) { toast.error("Cannot add assignee"); return; }
    setUpdatingAssignees(true);
    try {
      const payload = { userId: user.id, jobTaskId: note.taskId, assignedById: parseInt(userId) };
      const response = await fetch(`${apiUrl}/api/UserJobTaskAssignments`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (!response.ok && response.status !== 409) throw new Error("Failed to add assignee");
      setSelectedAssignees(prev => [...prev, { id: user.id, name: user.name }]);
      toast.success(`${user.name} assigned successfully`);
      if (onTaskAssigneeSelect) onTaskAssigneeSelect(note, { assignees: [...selectedAssignees, user] });
      if (refreshNotes) refreshNotes();
    } catch (error) {
      toast.error(`Failed to assign ${user.name}`);
    } finally {
      setUpdatingAssignees(false);
    }
  }, [note.taskId, userId, apiUrl, onTaskAssigneeSelect, refreshNotes, selectedAssignees]);

  const removeAssignee = useCallback(async (user) => {
    if (!note.taskId) { toast.error("Cannot remove assignee"); return; }
    setUpdatingAssignees(true);
    try {
      const response = await fetch(`${apiUrl}/api/UserJobTaskAssignments/task/${note.taskId}/user/${user.id}`, { method: "DELETE" });
      if (!response.ok && response.status !== 404) throw new Error("Failed to remove assignee");
      setSelectedAssignees(prev => prev.filter(a => a.id !== user.id));
      toast.success(`${user.name} removed successfully`);
      if (onTaskAssigneeSelect) onTaskAssigneeSelect(note, { assignees: selectedAssignees.filter(a => a.id !== user.id) });
      if (refreshNotes) refreshNotes();
    } catch (error) {
      toast.error(`Failed to remove ${user.name}`);
    } finally {
      setUpdatingAssignees(false);
    }
  }, [note.taskId, apiUrl, onTaskAssigneeSelect, refreshNotes, selectedAssignees]);

  const fetchUsers = useCallback(async () => {
    if (!userId) { setUsersError("User ID not available"); return; }
    setLoadingUsersList(true);
    setUsersError(null);
    try {
      const url = `${apiUrl}/api/Permissions/permissions-by-job/${job?.jobId || note.jobId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
      const data = await response.json();
      if (data && data.permissions && Array.isArray(data.permissions)) {
        const uniqueUsersMap = new Map();
        data.permissions.forEach(user => {
          if (!uniqueUsersMap.has(user.userId)) {
            uniqueUsersMap.set(user.userId, { id: user.userId, name: user.userName, color: getRandomColor(user.userId) });
          }
        });
        setAvailableUsers(Array.from(uniqueUsersMap.values()));
      } else {
        setAvailableUsers([]);
      }
    } catch (error) {
      console.error(error);
      setUsersError(error.message);
      toast.error("Failed to load users list");
    } finally {
      setLoadingUsersList(false);
    }
  }, [userId, apiUrl, job, note.jobId]);

  useEffect(() => {
    if (activePopup === "assignee") fetchUsers();
  }, [activePopup, fetchUsers]);

  // ========== Popup event handlers ==========
  const openPopup = (popupName) => {
    setActivePopup(popupName);
    if (popupName === "dueDate") {
      setCalendarMode("dueDate");
      setTempStartDate(null);
      setTempEndDate(null);
    }
  };
  const closePopup = () => setActivePopup(null);

  const handleDueDateSave = async (date) => {
    setSelectedDueDate(date);
    closePopup();
    const toastId = toast.loading(date ? "Updating due date..." : "Clearing due date...");
    await updateTask({ dueDate: date });
    toast.dismiss(toastId);
  };

  const handleDurationSave = async () => {
    if (tempStartDate && tempEndDate) {
      setSelectedStartDate(tempStartDate);
      setSelectedEndDate(tempEndDate);
      closePopup();
      const toastId = toast.loading("Updating duration...");
      await updateTask({ startDate: tempStartDate.toISOString(), endDate: tempEndDate.toISOString(), dueDate: null });
      toast.dismiss(toastId);
      setTempStartDate(null);
      setTempEndDate(null);
    } else {
      toast.error("Please select both start and end dates");
    }
  };

  const handleAssigneeSelect = async (user) => {
    const isAlreadyAssigned = selectedAssignees.some(a => a.id === user.id);
    if (isAlreadyAssigned) await removeAssignee(user);
    else await addAssignee(user);
    closePopup();
  };

  const handleStatusSelect = async (status) => {
    setSelectedStatus(status);
    closePopup();
    const toastId = toast.loading(`Changing status to ${status.name}...`);
    await updateTask({ status: status.id });
    toast.dismiss(toastId);
  };

  const goToPrevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  const handleDateSelect = (date) => {
    if (calendarMode === "dueDate") {
      handleDueDateSave(date);
    } else {
      if (!tempStartDate) { setTempStartDate(date); setTempEndDate(null); }
      else if (!tempEndDate && date >= tempStartDate) setTempEndDate(date);
      else { setTempStartDate(date); setTempEndDate(null); }
    }
  };
  const toggleCalendarMode = (mode) => setCalendarMode(mode);

  // ========== Render helpers ==========
  const renderStatusIcon = () => {
    const status = selectedStatus;
    const color = status?.color || "#6c757d";
    switch (status?.id) {
      case 1: return <i className="fas fa-check" style={{ fontSize: "14px", color }} />;
      case 2: return <i className="fas fa-check-double" style={{ fontSize: "14px", color }} />;
      case 3: return <i className="fas fa-archive" style={{ fontSize: "16px", color }} />;
      case 4: return <i className="fas fa-times" style={{ fontSize: "16px", color }} />;
      case 5: return <i className="fas fa-check-circle" style={{ fontSize: "16px", color }} />;
      default: return <i className="fas fa-check" style={{ fontSize: "14px", color: "#6c757d" }} />;
    }
  };

  const renderAssigneeAvatars = () => {
    if (selectedAssignees.length === 0) return <span style={{ color: "#999" }}>Unassigned</span>;
    return (
      <div style={{ display: "flex", fontWeight: "500", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
        {selectedAssignees.map(assignee => (
          <div key={assignee.id} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "2px 6px", backgroundColor: getRandomColor(assignee.id) + "20", borderRadius: "12px", fontSize: "11px" }} title={assignee.name}>
            <span style={{ maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{assignee.name}</span>
          </div>
        ))}
      </div>
    );
  };

  // ========== Context menu ==========
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
  const contextMenuRef = useRef(null);
  const handleNoteContentContextMenu = (e) => {
    e.preventDefault(); e.stopPropagation();
    let x = e.clientX, y = e.clientY;
    const menuWidth = 160, menuHeight = 50;
    if (x + menuWidth > window.innerWidth) x = window.innerWidth - menuWidth - 10;
    if (y + menuHeight > window.innerHeight) y = window.innerHeight - menuHeight - 10;
    setContextMenu({ visible: true, x, y });
  };
  const handleCopyNoteLink = async () => {
    const isTaskItem = note.itemType === "Task";
    const noteLink = isTaskItem ? `${window.location.origin}/dashboard?TaskId=${note.taskId || note.id}` : `${window.location.origin}/dashboard?SitenoteId=${note.id}`;
    try { await navigator.clipboard.writeText(noteLink); toast.success(`${isTaskItem ? "Task" : "Note"} link copied!`); }
    catch (err) { toast.error("Failed to copy link"); }
    setContextMenu({ visible: false, x: 0, y: 0 });
  };
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contextMenu.visible && contextMenuRef.current && !contextMenuRef.current.contains(e.target))
        setContextMenu({ visible: false, x: 0, y: 0 });
    };
    if (contextMenu.visible) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [contextMenu.visible]);

  // ========== Main render ==========
  return (
    <>
      <div
        ref={ref}
        data-note-id={note.id}
        data-item-type={note.itemType}
        className={`note-card ${isTask ? "task-card" : ""} ${selectedRow === note.id ? "selected" : ""}`}
        onClick={() => handleRowClick(note)}
        onDoubleClick={() => handleEdit(note)}
      >
        <NoteHeader
          note={note} searchTerm={searchTerm} userStatusMap={userStatusMap} loadingUsers={loadingUsers} viewMode={viewMode}
          handleJobClick={handleJobClick} handleTaskClick={handleTaskClick} handleUserNameClick={handleUserNameClick}
          isProjectFiltered={isProjectFiltered} isJobFiltered={isJobFiltered} isTaskFiltered={isTaskFiltered}
          isUserNameFiltered={isUserNameFiltered} showJobTooltip={showJobTooltip} setShowJobTooltip={setShowJobTooltip}
          showProjectTooltip={showProjectTooltip} setShowProjectTooltip={setShowProjectTooltip} onFilterByProject={onFilterByProject}
        />
        <NoteContent
          note={note} searchTerm={searchTerm} viewMode={viewMode}
          onMouseEnter={handleNoteTextMouseEnter} onMouseLeave={handleNoteTextMouseLeave}
          shouldShowNotePopup={shouldShowNotePopup} onContextMenu={handleNoteContentContextMenu}
        />
        <NoteFooter
          note={note} priorityValue={priorityValue} shouldShowLink={shouldShowLink} originalNoteId={originalNoteId}
          onViewAttachments={handleViewAttachments} onReply={handleReplyToNote} onAdd={handleAddFromRow} onEdit={handleRowDoubleClick}
          onDelete={handleDelete} onPriorityClick={handlePriorityClick} onPriorityMouseEnter={handlePriorityMouseEnter}
          onPriorityMouseLeave={handlePriorityMouseLeave} onLinkedNoteClick={handleLinkedNoteClick}
          onLinkedNoteMouseEnter={handleLinkedNoteMouseEnter} onLinkedNoteMouseLeave={handleLinkedNoteMouseLeave}
          renderImageIcon={renderCardImageIcon} isTask={isTask}
          selectedDueDate={selectedDueDate} selectedStartDate={selectedStartDate} selectedEndDate={selectedEndDate}
          selectedAssignees={selectedAssignees} selectedStatus={selectedStatus}
          onDueDateClick={() => openPopup("dueDate")} onAssigneeClick={() => openPopup("assignee")} onStatusClick={() => openPopup("status")}
          calendarIconRef={calendarIconRef} assigneeIconRef={assigneeIconRef} statusIconRef={statusIconRef}
          updatingTask={updatingTask} updatingAssignees={updatingAssignees}
          renderStatusIcon={renderStatusIcon} renderAssigneeAvatars={renderAssigneeAvatars}
        />
      </div>

      {/* Popup Portals - only render when position is ready */}
{activePopup === "dueDate" && calendarPosition && (
  <PopupPortal isOpen={true} onClose={closePopup} position={calendarPosition} loading={updatingTask} width={280}>
    <CalendarPopup
      currentMonth={currentMonth} onPrevMonth={goToPrevMonth} onNextMonth={goToNextMonth}
      selectedDueDate={selectedDueDate} selectedStartDate={selectedStartDate} selectedEndDate={selectedEndDate}
      tempStartDate={tempStartDate} tempEndDate={tempEndDate} calendarMode={calendarMode}
      onToggleMode={toggleCalendarMode} onDateSelect={handleDateSelect} onSave={calendarMode === "dueDate" ? () => {} : handleDurationSave}
    />
  </PopupPortal>
)}

{activePopup === "assignee" && assigneePosition && (
  <PopupPortal isOpen={true} onClose={closePopup} position={assigneePosition} loading={updatingAssignees} width={240}>
    <AssigneePopup
      users={availableUsers} selectedAssignees={selectedAssignees} onSelect={handleAssigneeSelect}
      getInitials={getInitialsHelper} getRandomColor={getRandomColor} loading={loadingUsersList}
    />
  </PopupPortal>
)}

{activePopup === "status" && statusPosition && (
  <PopupPortal isOpen={true} onClose={closePopup} position={statusPosition} loading={updatingTask} width={200}>
    <StatusPopup statuses={taskStatuses} selectedStatus={selectedStatus} onSelect={handleStatusSelect} />
  </PopupPortal>
)}

      {/* Context menu */}
      {contextMenu.visible && (
        <div ref={contextMenuRef} style={{ position: "fixed", top: `${contextMenu.y}px`, left: `${contextMenu.x}px`, zIndex: 999999, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", minWidth: "160px", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
          <div onClick={handleCopyNoteLink} style={{ padding: "10px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px", fontSize: "14px", color: "#333" }} onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f5f5f5"} onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}>
            <i className="fas fa-link" style={{ fontSize: "14px", color: "#666" }} /> <span>Copy link</span>
          </div>
        </div>
      )}
    </>
  );
});

// ========== Subcomponents (unchanged from original) ==========

const NoteHeader = ({
  note,
  searchTerm,
  viewMode,
  userStatusMap,
  loadingUsers,
  handleJobClick,
  handleTaskClick,
  handleUserNameClick,
  isProjectFiltered,
  isJobFiltered,
  isTaskFiltered,
  isUserNameFiltered,
  showJobTooltip,
  setShowJobTooltip,
  showProjectTooltip,
  setShowProjectTooltip,
  onFilterByProject,
}) => {
  const siteNoteFriendlyId =
    note.siteNoteFriendlyId || note.SiteNoteFriendlyId || "";
  const showSiteNoteFriendlyId =
    viewMode === "stacked" && siteNoteFriendlyId && note.itemType !== "Task";

  return (
    <div className="note-header">
      {showSiteNoteFriendlyId && (
        <div className="note-friendly-id-top" title={`Site note ID: ${siteNoteFriendlyId}`}>
          <i className="fas fa-hashtag" style={{ fontSize: "9px", opacity: 0.7 }} />
          {siteNoteFriendlyId}
        </div>
      )}
      <div className="note-header-main">
        <UserAvatar
          note={note}
          handleUserNameClick={handleUserNameClick}
          isUserNameFiltered={isUserNameFiltered}
        />
        <NoteMeta
          note={note}
          userStatusMap={userStatusMap}
          loadingUsers={loadingUsers}
          handleUserNameClick={handleUserNameClick}
          isUserNameFiltered={isUserNameFiltered}
        />
        <NoteContext
          note={note}
          searchTerm={searchTerm}
          handleJobClick={handleJobClick}
          handleTaskClick={handleTaskClick}
          isProjectFiltered={isProjectFiltered}
          isJobFiltered={isJobFiltered}
          isTaskFiltered={isTaskFiltered}
          showJobTooltip={showJobTooltip}
          setShowJobTooltip={setShowJobTooltip}
          showProjectTooltip={showProjectTooltip}
          setShowProjectTooltip={setShowProjectTooltip}
          onFilterByProject={onFilterByProject}
        />
      </div>
    </div>
  );
};

const UserAvatar = ({ note, handleUserNameClick, isUserNameFiltered }) => {
  const initials = getNoteInitials(note.userName);

  const userNameFiltered =
    isUserNameFiltered && typeof isUserNameFiltered === "function"
      ? isUserNameFiltered(note.userName)
      : false;

  const handleClick = (e) => {
    e.stopPropagation();
    if (handleUserNameClick && note.userName) {
      handleUserNameClick(note.userName);
    }
  };

  return (
    <div
      className="user-avatar-wrapper"
      onClick={handleClick}
      style={{
        cursor: handleUserNameClick ? "pointer" : "default",
        position: "relative",
      }}
      title={`Click to filter by user: ${note.userName || "Unknown User"}`}
    >
      <div
        className="user-avatar"
        style={{
          border: userNameFiltered ? "2px solid #3498db" : "none",
          boxShadow: userNameFiltered
            ? "0 0 0 2px rgba(52, 152, 219, 0.3)"
            : "none",
          transition: "all 0.2s ease",
        }}
      >
        {initials ? initials : <i className="fas fa-user" />}
      </div>
      <div className="user-tooltip">{note.userName || "Unknown User"}</div>
    </div>
  );
};

const NoteMeta = ({
  note,
  viewMode,
  userStatusMap,
  loadingUsers,
  handleUserNameClick,
  isUserNameFiltered,
}) => {
  const userNameFiltered =
    isUserNameFiltered && typeof isUserNameFiltered === "function"
      ? isUserNameFiltered(note.userName)
      : false;

  const handleUserNameClickHandler = (e) => {
    e.stopPropagation();
    if (handleUserNameClick && note.userId) {
      handleUserNameClick(note.userId);
    }
  };

  return (
    <div
      className="note-meta"
      style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}
    >
      <div
        className="note-author"
        title={note.userName || "Unknown User"}
        onClick={handleUserNameClickHandler}
        style={{
          cursor: handleUserNameClick ? "pointer" : "default",
          color: userNameFiltered ? "#3498db" : "inherit",
          fontWeight: userNameFiltered ? "600" : "700",
          textDecoration: userNameFiltered ? "underline" : "none",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "2px 4px",
          borderRadius: "3px",
          backgroundColor: userNameFiltered
            ? "rgba(52, 152, 219, 0.1)"
            : "transparent",
          transition: "all 0.2s ease",
        }}
      >
        <UserStatusIndicator
          userId={note.userId}
          userName={note.userName}
          userStatusMap={userStatusMap}
          loadingUsers={loadingUsers}
        />
      </div>
      <div className="note-date" title={getFullDate(note.timeStamp)}>
        {formatRelativeTime(note.timeStamp)}
      </div>
    </div>
  );
};

const NoteContext = ({
  note,
  searchTerm,
  handleJobClick,
  handleTaskClick,
  isProjectFiltered,
  isJobFiltered,
  isTaskFiltered,
  onFilterByProject,
}) => {
  const handleProjectClick = (e, projectId, projectName) => {
    e.stopPropagation();
    if (projectId && onFilterByProject) {
      onFilterByProject(projectId, projectName);
    }
  };

  const projectFiltered =
    isProjectFiltered && typeof isProjectFiltered === "function"
      ? isProjectFiltered(note.project)
      : false;

  const jobFiltered =
    isJobFiltered && typeof isJobFiltered === "function"
      ? isJobFiltered(note.job)
      : false;

  const taskFiltered =
    isTaskFiltered && typeof isTaskFiltered === "function"
      ? isTaskFiltered(note.title || note.note)
      : false;

  const isTask = note.itemType === "Task";
  const taskName = note.title || "";

  return (
    <div className="note-context">
      {taskName && (
        <span
          className="task-name"
          style={{
            color: "#000",
            fontWeight: "700",
            fontSize: "12px",
            maxWidth: "120px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
            verticalAlign: "middle",
          }}
          title={taskName}
          onClick={(e) => {
            e.stopPropagation();
            if (handleTaskClick && note.title) {
              handleTaskClick(note.title);
            }
          }}
        >
          {taskName.length > 30 ? `${taskName.substring(0, 30)}...` : taskName}
        </span>
      )}
      <div
        className="context-item job"
        title={`Click to filter by job: ${note.job}`}
        onClick={(e) => {
          e.stopPropagation();
          if (handleJobClick && note.job) {
            handleJobClick(note.job);
          }
        }}
        style={{
          cursor: "pointer",
          color: jobFiltered ? "#3498db" : "inherit",
          fontWeight: jobFiltered ? "600" : "normal",
          textDecoration: jobFiltered ? "underline" : "none",
        }}
      >
        {note.job || "—"}
      </div>
      <div className="context-item workspace-project">
        <span
          style={{
            maxWidth: "50px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "bottom",
          }}
          title={note.workspace}
        >
          {note.workspace || "—"}
        </span>
        /
        <span
          title={`Click to filter by project: ${note.project}`}
          onClick={(e) =>
            handleProjectClick(e, note.projectId, note.projectName)
          }
          style={{
            cursor: "pointer",
            fontWeight: projectFiltered ? "650" : "normal",
            textDecoration: projectFiltered ? "underline" : "none",
            display: "inline-block",
            maxWidth: "80px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            verticalAlign: "bottom",
          }}
        >
          {note.project || "—"}
        </span>
      </div>
    </div>
  );
};

const NoteContent = ({
  note,
  searchTerm,
  viewMode,
  onMouseEnter,
  onMouseLeave,
  shouldShowNotePopup,
  onContextMenu,
}) => {
  const handleMouseEnter = (e) => {
    if (
      typeof shouldShowNotePopup === "function" &&
      shouldShowNotePopup(note)
    ) {
      onMouseEnter(note, e);
    }
  };

  const handleMouseLeave = () => {
    if (
      typeof shouldShowNotePopup === "function" &&
      shouldShowNotePopup(note)
    ) {
      onMouseLeave();
    }
  };

  const isTask = note.itemType === "Task";
  const contentToDisplay = isTask ? note.description || "" : note.note || "";

  return (
    <div className="note-content">
      <div
        className="note-card-content-container"
        style={{ position: "relative", height: "100%" }}
        onClick={(e) => {
          if (
            e.target.tagName === "A" &&
            e.target.classList.contains("note-url-link")
          ) {
            e.stopPropagation();
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onContextMenu={onContextMenu}
      >
        <div
          className="note-text"
          style={{
            maxHeight: "80px",
            overflow: "hidden",
            position: "relative",
          }}
          dangerouslySetInnerHTML={{
            __html: highlightHtmlContent(contentToDisplay, searchTerm),
          }}
        />
      </div>
    </div>
  );
};

const NoteFooter = ({
  note,
  priorityValue,
  shouldShowLink,
  originalNoteId,
  onViewAttachments,
  onReply,
  onAdd,
  onEdit,
  onDelete,
  onPriorityClick,
  onPriorityMouseEnter,
  onPriorityMouseLeave,
  onLinkedNoteClick,
  onLinkedNoteMouseEnter,
  onLinkedNoteMouseLeave,
  renderImageIcon,
  isTask,
  selectedDueDate,
  selectedStartDate,
  selectedEndDate,
  selectedAssignees,
  selectedStatus,
  onDueDateClick,
  onAssigneeClick,
  onStatusClick,
  calendarIconRef,
  assigneeIconRef,
  statusIconRef,
  updatingTask,
  updatingAssignees,
  renderStatusIcon,
  renderAssigneeAvatars,
}) => {
  const dueDate = selectedDueDate ? new Date(selectedDueDate) : null;
  const startDate = selectedStartDate ? new Date(selectedStartDate) : null;
  const endDate = selectedEndDate ? new Date(selectedEndDate) : null;

  return (
    <div className="note-footer">
      <div className="note-attachments">
        {isTask ? (
          <>
            <div
              ref={calendarIconRef}
              style={{ display: "inline-block", position: "relative" }}
            >
              <div
                className={`task-icon-item ${selectedDueDate || (selectedStartDate && selectedEndDate) ? "has-value" : ""} ${updatingTask ? "updating" : ""}`}
                title={
                  selectedDueDate
                    ? `Due: ${dueDate.toLocaleDateString()}`
                    : selectedStartDate && selectedEndDate
                      ? `Duration: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                      : "Set Due Date or Duration"
                }
                onClick={onDueDateClick}
                style={{
                  opacity: updatingTask ? 0.5 : 1,
                  cursor: updatingTask ? "not-allowed" : "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  backgroundColor:
                    selectedDueDate || (selectedStartDate && selectedEndDate)
                      ? "#e3f2fd"
                      : "transparent",
                  transition: "all 0.2s ease",
                }}
              >
                <i
                  className="fas fa-calendar"
                  style={{
                    color:
                      selectedDueDate || (selectedStartDate && selectedEndDate)
                        ? "#1976d2"
                        : "#666",
                  }}
                ></i>
              </div>
            </div>

            <div
              ref={assigneeIconRef}
              style={{ display: "inline-block", position: "relative" }}
            >
              <div
                className={`task-icon-item ${selectedAssignees.length > 0 ? "has-value" : ""} ${updatingAssignees ? "updating" : ""}`}
                title={`Assignees: ${selectedAssignees.length > 0 ? selectedAssignees.map((a) => a.name).join(", ") : "Unassigned"}`}
                onClick={onAssigneeClick}
                style={{
                  opacity: updatingAssignees ? 0.5 : 1,
                  cursor: updatingAssignees ? "not-allowed" : "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  backgroundColor:
                    selectedAssignees.length > 0 ? "#e3f2fd" : "transparent",
                  transition: "all 0.2s ease",
                  position: "relative",
                }}
              >
                <i
                  className="fas fa-user"
                  style={{
                    color: selectedAssignees.length > 0 ? "#1976d2" : "#666",
                  }}
                ></i>
                {selectedAssignees.length > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "-4px",
                      right: "-4px",
                      backgroundColor: "#fd7e14",
                      color: "white",
                      borderRadius: "50%",
                      width: "14px",
                      height: "14px",
                      fontSize: "9px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedAssignees.length}
                  </span>
                )}
              </div>
            </div>

            <div
              ref={statusIconRef}
              style={{ display: "inline-block", position: "relative" }}
            >
              <div
                className={`task-icon-item ${selectedStatus ? "has-value" : ""} ${updatingTask ? "updating" : ""}`}
                title={`Status: ${selectedStatus?.name || "To Do"}`}
                onClick={onStatusClick}
                style={{
                  opacity: updatingTask ? 0.5 : 1,
                  cursor: updatingTask ? "not-allowed" : "pointer",
                  padding: "4px",
                  minWidth: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  backgroundColor: selectedStatus
                    ? `${selectedStatus.bgColor}`
                    : "transparent",
                  transition: "all 0.2s ease",
                }}
              >
                {renderStatusIcon()}
              </div>
            </div>
          </>
        ) : (
          <>
            {renderImageIcon && renderImageIcon(note)}
            <AttachmentButton note={note} onClick={onViewAttachments} />
            <ReplyButton note={note} onClick={onReply} />
            {shouldShowLink && originalNoteId && (
              <LinkButton
                noteId={note.id}
                onClick={onLinkedNoteClick}
                onMouseEnter={onLinkedNoteMouseEnter}
                onMouseLeave={onLinkedNoteMouseLeave}
              />
            )}
          </>
        )}
      </div>

      <div className="note-actions">
        {!isTask && (
          <PriorityIndicator
            priorityValue={priorityValue}
            note={note}
            onClick={onPriorityClick}
            onMouseEnter={onPriorityMouseEnter}
            onMouseLeave={onPriorityMouseLeave}
          />
        )}

        <ActionButton
          icon="fas fa-plus"
          title="Add New Note"
          onClick={() => {
            onAdd(note);
          }}
        />

        {!isTask && (
          <ActionButton
            icon="fas fa-external-link-alt"
            title="View Note"
            onClick={() => onEdit(note)}
          />
        )}

        {!isTask && (
          <ActionButton
            icon="fas fa-trash"
            title="Delete Note"
            onClick={() => onDelete(note)}
          />
        )}
      </div>
    </div>
  );
};

const AttachmentButton = ({ note, onClick }) => (
  <button
    className="attachment-btn"
    onClick={(e) => {
      e.stopPropagation();
      onClick(note);
    }}
  >
    <i
      className="fas fa-paperclip"
      style={{ opacity: note.documentCount > 0 ? 1 : 0.3 }}
    />
    <span>({note.documentCount || 0})</span>
  </button>
);

const ReplyButton = ({ note, onClick }) => (
  <button
    className="attachment-btn"
    onClick={(e) => {
      e.stopPropagation();
      onClick(note);
    }}
    title="Reply to this note"
  >
    <i className="fas fa-reply" />
  </button>
);

const LinkButton = ({ noteId, onClick, onMouseEnter, onMouseLeave }) => (
  <button
    className="link-to-note-btn"
    onClick={(e) => onClick(noteId, e)}
    onMouseEnter={(e) => onMouseEnter(noteId, e)}
    onMouseLeave={onMouseLeave}
  >
    <i className="fas fa-link" />
  </button>
);

const ActionButton = ({ icon, title, onClick }) => (
  <button
    className="action-btn"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    title={title}
  >
    <i className={icon} />
  </button>
);

// ========== PropTypes and defaultProps ==========
NoteCard.propTypes = {
  note: PropTypes.object.isRequired,
  displayNotes: PropTypes.array,
  isOriginalNoteExists: PropTypes.func,
  selectedRow: PropTypes.any,
  searchTerm: PropTypes.string,
  viewMode: PropTypes.string,
  handleRowClick: PropTypes.func.isRequired,
  handleRowDoubleClick: PropTypes.func.isRequired,
  handleAddFromRow: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleViewAttachments: PropTypes.func.isRequired,
  handleReplyToNote: PropTypes.func.isRequired,
  handlePriorityClick: PropTypes.func.isRequired,
  handlePriorityMouseEnter: PropTypes.func.isRequired,
  handlePriorityMouseLeave: PropTypes.func.isRequired,
  handleNoteTextMouseEnter: PropTypes.func.isRequired,
  handleNoteTextMouseLeave: PropTypes.func.isRequired,
  handleLinkedNoteClick: PropTypes.func.isRequired,
  handleLinkedNoteMouseEnter: PropTypes.func.isRequired,
  handleLinkedNoteMouseLeave: PropTypes.func.isRequired,
  renderCardImageIcon: PropTypes.func,
  isNoteReply: PropTypes.func.isRequired,
  getReplyNoteId: PropTypes.func.isRequired,
  userStatusMap: PropTypes.object.isRequired,
  loadingUsers: PropTypes.object.isRequired,
  getPriorityValue: PropTypes.func.isRequired,
  manuallyUpdatedPriorities: PropTypes.object.isRequired,
  shouldShowNotePopup: PropTypes.func.isRequired,
  handleProjectClick: PropTypes.func,
  handleJobClick: PropTypes.func,
  handleTaskClick: PropTypes.func,
  handleUserNameClick: PropTypes.func,
  isUserNameFiltered: PropTypes.func,
  isProjectFiltered: PropTypes.func,
  isJobFiltered: PropTypes.func,
  isTaskFiltered: PropTypes.func,
  taskData: PropTypes.object,
  onTaskDueDateSelect: PropTypes.func,
  onTaskAssigneeSelect: PropTypes.func,
  onTaskStatusSelect: PropTypes.func,
  taskUsers: PropTypes.array,
  taskStatuses: PropTypes.array,
  loadingTaskUsers: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshNotes: PropTypes.func,
  job: PropTypes.object,
};

NoteCard.defaultProps = {
  displayNotes: [],
  isOriginalNoteExists: () => false,
  selectedRow: null,
  searchTerm: "",
  viewMode: "cards",
  renderCardImageIcon: () => null,
  handleProjectClick: () => {},
  handleJobClick: () => {},
  handleTaskClick: () => {},
  handleUserNameClick: () => {},
  isProjectFiltered: () => false,
  isJobFiltered: () => false,
  isTaskFiltered: () => false,
  isUserNameFiltered: () => false,
  taskData: {},
  onTaskDueDateSelect: () => {},
  onTaskAssigneeSelect: () => {},
  onTaskStatusSelect: () => {},
  taskUsers: [],
  taskStatuses: [],
  loadingTaskUsers: false,
  refreshNotes: () => {},
  job: null,
};

export default NoteCard;